import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { Vehicle } from "./Data/ApiTypes";
import { ReservationVehicleCard } from "./ReservationVehicleCard";
import {
  ParkerIntegrationPartyApi,
  ServerRequestsParkerIntegrationPartyDto,
} from "./shared/api/axios-client";
import { createApiService, silentAxiosRequest } from "./shared/apiConfig";
import { gridOneCol } from "./Data/Common";
import { PageBar } from "./Controls/PageBar";
import { supportsRealTimeSyncCheck } from "./Data/IntegrationCapabilities";
import { CarFrontFill } from "react-bootstrap-icons";

export type VehicleSyncStatus = "verifying" | "verified" | "not_verified" | "error";

export interface VehicleSyncState {
  status: VehicleSyncStatus;
  message?: string | null;
  canResync?: boolean;
}

export function vehicleIntegrationSyncKey(
  integrationPartyId: number,
  vehicleId: number
) {
  return `${integrationPartyId}-${vehicleId}`;
}

type Props = {
  vehicles: Vehicle[];
  subscriptionStatus: SubscriptionStatus;
  facilityId: number;
  subscriptionId: number;
  parkerId: number;
  integrations: ServerRequestsParkerIntegrationPartyDto[];
  onAddNew(): void;
  onAddExisting(): void;
  onRemove(vehicle: Vehicle): void;
  onResyncVehicleIntegration?: (
    integrationPartyId: number,
    parkerId: number,
    vehicleId: number
  ) => Promise<void>;
  setPendingToggle: React.Dispatch<React.SetStateAction<any>>;
  setShowToggleConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  showVehiclesLabel?: boolean;
};

export const VehiclesSection = ({
  vehicles,
  subscriptionStatus,
  facilityId,
  subscriptionId,
  parkerId,
  integrations,
  onAddNew,
  onAddExisting,
  onRemove,
  onResyncVehicleIntegration,
  setPendingToggle,
  setShowToggleConfirm,
  showVehiclesLabel = false,
}: Props) => {
  const [syncStates, setSyncStates] = useState<Record<string, VehicleSyncState>>({});
  const [resyncingKey, setResyncingKey] = useState<string | null>(null);

  const eligibleIntegrations = useMemo(
    () =>
      integrations.filter(
        (i) =>
          supportsRealTimeSyncCheck(i) &&
          i.isActive &&
          i.integrationPartyId != null &&
          i.vehicleId != null
      ),
    [integrations]
  );

  const syncSignature = useMemo(
    () =>
      eligibleIntegrations
        .map(
          (i) =>
            `${i.integrationPartyId}:${i.vehicleId}:${i.isActive}:${i.parkerId}`
        )
        .sort()
        .join("|"),
    [eligibleIntegrations]
  );

  const fetchSyncStatus = useCallback(async () => {
    if (eligibleIntegrations.length === 0 || parkerId == null) return;

    const keys = eligibleIntegrations.map((i) =>
      vehicleIntegrationSyncKey(i.integrationPartyId!, i.vehicleId!)
    );

    setSyncStates((prev) => {
      const next = { ...prev };
      for (const key of keys) {
        next[key] = { status: "verifying" };
      }
      return next;
    });

    try {
      const api = createApiService(ParkerIntegrationPartyApi);
      const response = await api.parkerIntegrationPartiesSyncStatusGet(
        {
          facilityId,
          subscriptionId,
          parkerId,
        },
        silentAxiosRequest
      );

      const items = response.data.items ?? [];
      setSyncStates((prev) => {
        const next = { ...prev };
        for (const integration of eligibleIntegrations) {
          const key = vehicleIntegrationSyncKey(
            integration.integrationPartyId!,
            integration.vehicleId!
          );
          const item = items.find(
            (i) =>
              i.integrationPartyId === integration.integrationPartyId &&
              i.vehicleId === integration.vehicleId
          );
          if (!item) {
            next[key] = {
              status: "error",
              message: "Verification unavailable",
              canResync: false,
            };
          } else if (item.isSynced) {
            next[key] = { status: "verified" };
          } else {
            next[key] = {
              status: "not_verified",
              message: item.message,
              canResync: item.canResync ?? false,
            };
          }
        }
        return next;
      });
    } catch {
      setSyncStates((prev) => {
        const next = { ...prev };
        for (const key of keys) {
          next[key] = {
            status: "error",
            message: "Verification failed",
            canResync: false,
          };
        }
        return next;
      });
    }
  }, [eligibleIntegrations, facilityId, subscriptionId, parkerId]);

  const fetchSyncStatusRef = useRef(fetchSyncStatus);
  fetchSyncStatusRef.current = fetchSyncStatus;

  useEffect(() => {
    if (!syncSignature) return;
    fetchSyncStatusRef.current();
  }, [syncSignature, facilityId, subscriptionId, parkerId]);

  const handleResync = async (
    integration: ServerRequestsParkerIntegrationPartyDto
  ) => {
    if (
      !onResyncVehicleIntegration ||
      integration.integrationPartyId == null ||
      integration.parkerId == null ||
      integration.vehicleId == null
    ) {
      return;
    }

    const key = vehicleIntegrationSyncKey(
      integration.integrationPartyId,
      integration.vehicleId
    );
    setResyncingKey(key);
    try {
      await onResyncVehicleIntegration(
        integration.integrationPartyId,
        integration.parkerId,
        integration.vehicleId
      );
      await fetchSyncStatus();
    } finally {
      setResyncingKey(null);
    }
  };

  const actions =
    subscriptionStatus === SubscriptionStatus.Active ? (
      <>
        <Link
          to=""
          onClick={(e) => {
            e.preventDefault();
            onAddNew();
          }}
        >
          Add New
        </Link>
        {vehicles.length > 0 && (
          <>
            &nbsp;|&nbsp;
            <Link
              to=""
              onClick={(e) => {
                e.preventDefault();
                onAddExisting();
              }}
            >
              Add Existing
            </Link>
          </>
        )}
      </>
    ) : null;

  const card = (
    <Card className="mb-2">
      {!showVehiclesLabel && (
        <Card.Header className="d-flex align-items-center justify-content-between gap-2 py-2">
          <div className="d-flex align-items-center gap-2 min-w-0">
            <CarFrontFill size={16} className="text-muted flex-shrink-0" />
            <span>Vehicles</span>
          </div>
          <div className="flex-shrink-0">{actions}</div>
        </Card.Header>
      )}

      <Card.Body>
        {vehicles.length === 0 ? (
          <div className="text-secondary">
            This parker hasn’t added any vehicles yet.
          </div>
        ) : (
          vehicles.map((v) => (
            <ReservationVehicleCard
              key={v.vehicleId}
              vehicle={v}
              subscriptionStatus={subscriptionStatus}
              facilityId={facilityId}
              subscriptionId={subscriptionId}
              onRemove={() => onRemove(v)}
              integrations={integrations}
              syncStates={syncStates}
              resyncingKey={resyncingKey}
              onResync={handleResync}
              setPendingToggle={setPendingToggle}
              setShowToggleConfirm={setShowToggleConfirm}
            />
          ))
        )}
      </Card.Body>
    </Card>
  );

  if (!showVehiclesLabel) {
    return card;
  }

  return (
    <Container fluid>
      <Row>
        <Col style={gridOneCol("max-content 1fr 5px")}>
          <Row className="align-items-center justify-content-between">
            <Col>
              <PageBar title="Vehicles" />
            </Col>
            <Col xs="auto">{actions}</Col>
          </Row>
          {card}
        </Col>
      </Row>
    </Container>
  );
};
